// frontend/src/pages/public/CCHOHT/CchohtLandingPage.tsx
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "./CchohtLandingPage.css";

type LangCode = "en" | "ur" | "zh-cn" | "zh-hk" | "es" | "pl" | "ar" | "pa" | "fr";

type CitySlug = "oakville" | "milton" | "halton-hills";

type City = {
  name: string;
  slug: CitySlug;
};

type Copy = {
  heroTitle: string;
  heroSub: string;
  poweredBy: string;
  step1Label: string;
  step1Title: string;
  step1Sub: string;
  step2Label: (city: string) => string;
  step2Title: string;
  step2Sub: string;
  browseTitle: string;
  browseSub: string;
  navTitle: string;
  navSub: string;
  changeCity: string;
  footerCopy: string;
  privacy: string;
  accessibility: string;
  popoverTitle: string;
  popoverBody: string;
};

type LangOption = {
  code: LangCode;
  native: string;
  dir: "ltr" | "rtl";
  htmlLang: string;
};

const CITIES: City[] = [
  { name: "Oakville", slug: "oakville" },
  { name: "Milton", slug: "milton" },
  { name: "Halton Hills", slug: "halton-hills" },
];

const SEARCH_PATH: Record<CitySlug, string> = {
  oakville: "/public/find-family-doctor/oakville",
  milton: "/public/find-family-doctor/milton",
  "halton-hills": "/public/find-family-doctor/halton-hills",
};

const NAVIGATOR_PATH = "/public/community-navigator-help";

const LANGS: LangOption[] = [
  { code: "en", native: "English", dir: "ltr", htmlLang: "en" },
  { code: "ur", native: "اردو", dir: "rtl", htmlLang: "ur" },
  { code: "zh-cn", native: "普通话", dir: "ltr", htmlLang: "zh-CN" },
  { code: "zh-hk", native: "廣東話", dir: "ltr", htmlLang: "zh-HK" },
  { code: "es", native: "Español", dir: "ltr", htmlLang: "es" },
  { code: "pl", native: "Polski", dir: "ltr", htmlLang: "pl" },
  { code: "ar", native: "العربية", dir: "rtl", htmlLang: "ar" },
  { code: "pa", native: "ਪੰਜਾਬੀ", dir: "ltr", htmlLang: "pa" },
  { code: "fr", native: "Français", dir: "ltr", htmlLang: "fr" },
];

// Step 2 pathway copy, the navigator popover, and Privacy/Accessibility are translated per language.
// The footer copyright line stays English in every language.
const APPROVED_ENGLISH = {
  step2Sub: "Choose the option that works best for you.",
  browseTitle: "I want to search for a family doctor myself",
  browseSub:
    "View participating family doctors accepting new patient requests and submit a request directly.",
  navTitle: "I'd like help from a Community Navigator",
  navSub:
    "A Community Navigator can help identify participating family doctors that may fit your needs and guide you through the request process.",
  footerCopy: "© 2026 Connected Care Halton Ontario Health Team",
  privacy: "Privacy",
  accessibility: "Accessibility",
  popoverTitle: "What is a Community Navigator?",
  popoverBody:
    "A Community Navigator works with Connected Care Halton to help people who need assistance finding a family doctor. They can speak with you about your needs, help identify participating family doctors that may be a good fit, and guide you through the request process. The family doctor or clinic makes the final decision about accepting new patients.",
};

const COPY: Record<LangCode, Copy> = {
  en: {
    ...APPROVED_ENGLISH,
    heroTitle: "Find a Family Doctor in Halton",
    heroSub:
      "Reducing health system barriers for the people of Oakville, Halton Hills and Milton.",
    poweredBy: "Powered by AcceptingMD.ca",
    step1Label: "Step 1 of 2",
    step1Title: "Where are you looking for a family doctor?",
    step1Sub:
      "This free service helps Halton residents find and connect with a family doctor accepting new patients.",
    step2Label: (city) => `Step 2 of 2 · ${city}`,
    step2Title: "How would you like to find a doctor?",
    changeCity: "← Change city",
  },
  ur: {
    ...APPROVED_ENGLISH,
    heroTitle: "ہالٹن میں فیملی ڈاکٹر تلاش کریں",
    heroSub: "اوک ویل، ہالٹن ہلز اور ملٹن کے لوگوں کے لیے صحت کی راہ میں رکاوٹیں کم کرنا۔",
    poweredBy: "AcceptingMD.ca کی مدد سے",
    step1Label: "مرحلہ 1 از 2",
    step1Title: "آپ کہاں فیملی ڈاکٹر تلاش کر رہے ہیں؟",
    step1Sub:
      "یہ مفت سروس ہالٹن کے باشندوں کو نئے مریضوں کو قبول کرنے والے فیملی ڈاکٹر سے جوڑنے میں مدد کرتی ہے۔",
    step2Label: (city) => `مرحلہ 2 از 2 · ${city}`,
    step2Title: "آپ ڈاکٹر کیسے تلاش کرنا چاہتے ہیں؟",
    changeCity: "← شہر تبدیل کریں",
    step2Sub: "اپنے لیے سب سے موزوں آپشن منتخب کریں۔",
    browseTitle: "میں خود فیملی ڈاکٹر تلاش کرنا چاہتا/چاہتی ہوں۔",
    browseSub:
      "ایسے شریک فیملی ڈاکٹروں کو دیکھیں جو نئے مریضوں کی درخواستیں قبول کر رہے ہیں اور براہِ راست درخواست جمع کریں۔",
    navTitle: "میں کمیونٹی نیویگیٹر سے مدد لینا چاہتا/چاہتی ہوں۔",
    navSub:
      "کمیونٹی نیویگیٹر ایسے شریک فیملی ڈاکٹروں کی نشاندہی میں آپ کی مدد کر سکتا ہے جو آپ کی ضروریات کے مطابق ہو سکتے ہیں، اور درخواست کے عمل میں آپ کی رہنمائی کر سکتا ہے۔",
    privacy: "رازداری",
    accessibility: "رسائی پذیری",
    popoverTitle: "کمیونٹی نیویگیٹر کیا ہے؟",
    popoverBody:
      "کمیونٹی نیویگیٹر Connected Care Halton کے ساتھ کام کرتا ہے تاکہ ان لوگوں کی مدد کی جا سکے جنہیں فیملی ڈاکٹر تلاش کرنے میں مدد درکار ہو۔ وہ آپ سے آپ کی ضروریات کے بارے میں بات کر سکتا ہے، ایسے شریک فیملی ڈاکٹروں کی نشاندہی میں مدد کر سکتا ہے جو آپ کے لیے مناسب ہو سکتے ہیں، اور درخواست کے عمل میں آپ کی رہنمائی کر سکتا ہے۔ نئے مریضوں کو قبول کرنے کا حتمی فیصلہ فیملی ڈاکٹر یا کلینک کرتا ہے۔",
  },
  "zh-cn": {
    ...APPROVED_ENGLISH,
    heroTitle: "在哈尔顿寻找家庭医生",
    heroSub: "为奥克维尔、哈尔顿山和米尔顿的居民减少医疗障碍。",
    poweredBy: "由 AcceptingMD.ca 提供支持",
    step1Label: "第 1 步，共 2 步",
    step1Title: "您在哪个城市寻找家庭医生？",
    step1Sub: "此免费服务帮助哈尔顿居民找到正在接受新患者的家庭医生。",
    step2Label: (city) => `第 2 步，共 2 步 · ${city}`,
    step2Title: "您希望如何寻找医生？",
    changeCity: "← 更换城市",
    step2Sub: "选择最适合您的方式。",
    browseTitle: "我想自己寻找家庭医生。",
    browseSub: "查看目前接受新患者申请的参与家庭医生，并直接提交申请。",
    navTitle: "我希望获得社区导航员的帮助。",
    navSub: "社区导航员可以帮助您找到可能符合您需求的参与家庭医生，并指导您完成申请流程。",
    privacy: "隐私",
    accessibility: "无障碍",
    popoverTitle: "什么是社区导航员？",
    popoverBody:
      "社区导航员与 Connected Care Halton 合作，为需要帮助寻找家庭医生的人提供支持。他们可以与您沟通并了解您的需求，帮助您找到可能适合您的参与家庭医生，并指导您完成申请流程。是否接收新患者的最终决定由家庭医生或诊所作出。",
  },
  "zh-hk": {
    ...APPROVED_ENGLISH,
    heroTitle: "在哈爾頓尋找家庭醫生",
    heroSub: "為奧克維爾、哈爾頓山和米爾頓的居民減少醫療障礙。",
    poweredBy: "由 AcceptingMD.ca 提供支援",
    step1Label: "第 1 步，共 2 步",
    step1Title: "您在哪個城市尋找家庭醫生？",
    step1Sub: "此免費服務幫助哈爾頓居民找到正在接受新病人的家庭醫生。",
    step2Label: (city) => `第 2 步，共 2 步 · ${city}`,
    step2Title: "您希望如何尋找醫生？",
    changeCity: "← 更換城市",
    step2Sub: "選擇最適合你嘅方式。",
    browseTitle: "我想自己搵家庭醫生。",
    browseSub: "查看現正接受新病人申請嘅參與家庭醫生，並直接提交申請。",
    navTitle: "我想由社區導航員協助。",
    navSub: "社區導航員可以協助你搵到可能適合你需要嘅參與家庭醫生，並指引你完成申請流程。",
    privacy: "私隱",
    accessibility: "無障礙",
    popoverTitle: "咩係社區導航員？",
    popoverBody:
      "社區導航員同 Connected Care Halton 合作，協助需要幫手搵家庭醫生嘅人士。佢哋可以同你傾下你嘅需要，幫你搵可能適合你嘅參與家庭醫生，並指引你完成申請流程。是否接收新病人，最終由家庭醫生或診所決定。",
  },
  es: {
    ...APPROVED_ENGLISH,
    heroTitle: "Encuentre un médico de familia en Halton",
    heroSub:
      "Reduciendo las barreras del sistema de salud para la gente de Oakville, Halton Hills y Milton.",
    poweredBy: "Desarrollado por AcceptingMD.ca",
    step1Label: "Paso 1 de 2",
    step1Title: "¿Dónde busca un médico de familia?",
    step1Sub:
      "Este servicio gratuito ayuda a los residentes de Halton a encontrar y conectarse con un médico de familia que acepta nuevos pacientes.",
    step2Label: (city) => `Paso 2 de 2 · ${city}`,
    step2Title: "¿Cómo le gustaría encontrar un médico?",
    changeCity: "← Cambiar ciudad",
    step2Sub: "Elija la opción que mejor se adapte a sus necesidades.",
    browseTitle: "Quiero buscar un médico de familia por mi cuenta.",
    browseSub:
      "Consulte los médicos de familia participantes que aceptan nuevas solicitudes de pacientes y envíe una solicitud directamente.",
    navTitle: "Me gustaría recibir ayuda de un Navegador Comunitario.",
    navSub:
      "Un Navegador Comunitario puede ayudarle a identificar médicos de familia participantes que podrían adaptarse a sus necesidades y orientarle durante el proceso de solicitud.",
    privacy: "Privacidad",
    accessibility: "Accesibilidad",
    popoverTitle: "¿Qué es un Navegador Comunitario?",
    popoverBody:
      "Un Navegador Comunitario trabaja con Connected Care Halton para ayudar a las personas que necesitan apoyo para encontrar un médico de familia. Puede hablar con usted sobre sus necesidades, ayudarle a identificar médicos de familia participantes que podrían ser adecuados para usted y orientarle durante el proceso de solicitud. El médico de familia o la clínica toma la decisión final sobre la aceptación de nuevos pacientes.",
  },
  pl: {
    ...APPROVED_ENGLISH,
    heroTitle: "Znajdź lekarza rodzinnego w Halton",
    heroSub:
      "Zmniejszanie barier w systemie opieki zdrowotnej dla mieszkańców Oakville, Halton Hills i Milton.",
    poweredBy: "Obsługiwane przez AcceptingMD.ca",
    step1Label: "Krok 1 z 2",
    step1Title: "Gdzie szukasz lekarza rodzinnego?",
    step1Sub:
      "Ta bezpłatna usługa pomaga mieszkańcom Halton znaleźć lekarza rodzinnego przyjmującego nowych pacjentów.",
    step2Label: (city) => `Krok 2 z 2 · ${city}`,
    step2Title: "Jak chciałbyś znaleźć lekarza?",
    changeCity: "← Zmień miasto",
    step2Sub: "Wybierz opcję, która najlepiej odpowiada Twoim potrzebom.",
    browseTitle: "Chcę samodzielnie znaleźć lekarza rodzinnego.",
    browseSub:
      "Zobacz uczestniczących lekarzy rodzinnych, którzy przyjmują nowe zgłoszenia pacjentów, i wyślij zgłoszenie bezpośrednio.",
    navTitle: "Chcę skorzystać z pomocy nawigatora społecznościowego.",
    navSub:
      "Nawigator społecznościowy może pomóc Ci znaleźć uczestniczących lekarzy rodzinnych, którzy mogą odpowiadać Twoim potrzebom, i przeprowadzić Cię przez proces zgłoszenia.",
    privacy: "Prywatność",
    accessibility: "Dostępność",
    popoverTitle: "Kim jest nawigator społecznościowy?",
    popoverBody:
      "Nawigator społecznościowy współpracuje z Connected Care Halton, aby pomagać osobom potrzebującym wsparcia w znalezieniu lekarza rodzinnego. Może porozmawiać z Tobą o Twoich potrzebach, pomóc znaleźć uczestniczących lekarzy rodzinnych, którzy mogą być dla Ciebie odpowiedni, oraz przeprowadzić Cię przez proces zgłoszenia. Ostateczną decyzję o przyjęciu nowych pacjentów podejmuje lekarz rodzinny lub klinika.",
  },
  ar: {
    ...APPROVED_ENGLISH,
    heroTitle: "ابحث عن طبيب عائلة في هالتون",
    heroSub: "تقليل عوائق نظام الرعاية الصحية لأهالي أوكفيل وهالتون هيلز وميلتون.",
    poweredBy: "مدعوم من AcceptingMD.ca",
    step1Label: "الخطوة 1 من 2",
    step1Title: "أين تبحث عن طبيب عائلة؟",
    step1Sub:
      "تساعد هذه الخدمة المجانية سكان هالتون في إيجاد طبيب عائلة يقبل مرضى جدداً والتواصل معه.",
    step2Label: (city) => `الخطوة 2 من 2 · ${city}`,
    step2Title: "كيف تريد إيجاد طبيب؟",
    changeCity: "← تغيير المدينة",
    step2Sub: "اختر الخيار الأنسب لك.",
    browseTitle: "أرغب في البحث عن طبيب أسرة بنفسي.",
    browseSub: "اطّلع على أطباء الأسرة المشاركين الذين يقبلون طلبات مرضى جدد، وقدّم طلبك مباشرة.",
    navTitle: "أرغب في الحصول على مساعدة من مرشد مجتمعي.",
    navSub:
      "يمكن للمرشد المجتمعي مساعدتك في التعرّف على أطباء أسرة مشاركين قد يكونون مناسبين لاحتياجاتك، وإرشادك خلال عملية تقديم الطلب.",
    privacy: "الخصوصية",
    accessibility: "إمكانية الوصول",
    popoverTitle: "ما هو المرشد المجتمعي؟",
    popoverBody:
      "يعمل المرشد المجتمعي مع Connected Care Halton لمساعدة الأشخاص الذين يحتاجون إلى دعم في العثور على طبيب أسرة. يمكنه التحدث معك حول احتياجاتك، ومساعدتك في التعرّف على أطباء أسرة مشاركين قد يكونون مناسبين لك، وإرشادك خلال عملية تقديم الطلب. ويتخذ طبيب الأسرة أو العيادة القرار النهائي بشأن قبول مرضى جدد.",
  },
  pa: {
    ...APPROVED_ENGLISH,
    heroTitle: "ਹੈਲਟਨ ਵਿੱਚ ਪਰਿਵਾਰਕ ਡਾਕਟਰ ਲੱਭੋ",
    heroSub: "ਓਕਵਿਲ, ਹੈਲਟਨ ਹਿੱਲਜ਼ ਅਤੇ ਮਿਲਟਨ ਦੇ ਲੋਕਾਂ ਲਈ ਸਿਹਤ ਸੇਵਾਵਾਂ ਵਿੱਚ ਰੁਕਾਵਟਾਂ ਘਟਾਉਣਾ।",
    poweredBy: "AcceptingMD.ca ਦੁਆਰਾ ਸੰਚਾਲਿਤ",
    step1Label: "ਕਦਮ 1 ਦਾ 2",
    step1Title: "ਤੁਸੀਂ ਕਿੱਥੇ ਪਰਿਵਾਰਕ ਡਾਕਟਰ ਲੱਭ ਰਹੇ ਹੋ?",
    step1Sub:
      "ਇਹ ਮੁਫਤ ਸੇਵਾ ਹੈਲਟਨ ਦੇ ਵਸਨੀਕਾਂ ਨੂੰ ਨਵੇਂ ਮਰੀਜ਼ਾਂ ਨੂੰ ਸਵੀਕਾਰ ਕਰਨ ਵਾਲੇ ਪਰਿਵਾਰਕ ਡਾਕਟਰ ਨਾਲ ਜੋੜਨ ਵਿੱਚ ਮਦਦ ਕਰਦੀ ਹੈ।",
    step2Label: (city) => `ਕਦਮ 2 ਦਾ 2 · ${city}`,
    step2Title: "ਤੁਸੀਂ ਡਾਕਟਰ ਕਿਵੇਂ ਲੱਭਣਾ ਚਾਹੁੰਦੇ ਹੋ?",
    changeCity: "← ਸ਼ਹਿਰ ਬਦਲੋ",
    step2Sub: "ਉਹ ਵਿਕਲਪ ਚੁਣੋ ਜੋ ਤੁਹਾਡੇ ਲਈ ਸਭ ਤੋਂ ਵਧੀਆ ਹੈ।",
    browseTitle: "ਮੈਂ ਖੁਦ ਫੈਮਿਲੀ ਡਾਕਟਰ ਲੱਭਣਾ ਚਾਹੁੰਦਾ/ਚਾਹੁੰਦੀ ਹਾਂ।",
    browseSub:
      "ਉਹ ਭਾਗੀਦਾਰ ਫੈਮਿਲੀ ਡਾਕਟਰ ਵੇਖੋ ਜੋ ਨਵੇਂ ਮਰੀਜ਼ਾਂ ਦੀਆਂ ਬੇਨਤੀਆਂ ਸਵੀਕਾਰ ਕਰ ਰਹੇ ਹਨ ਅਤੇ ਸਿੱਧੀ ਬੇਨਤੀ ਭੇਜੋ।",
    navTitle: "ਮੈਂ ਕਮਿਊਨਿਟੀ ਨੈਵੀਗੇਟਰ ਤੋਂ ਮਦਦ ਲੈਣਾ ਚਾਹੁੰਦਾ/ਚਾਹੁੰਦੀ ਹਾਂ।",
    navSub:
      "ਕਮਿਊਨਿਟੀ ਨੈਵੀਗੇਟਰ ਤੁਹਾਡੀਆਂ ਲੋੜਾਂ ਅਨੁਸਾਰ ਢੁੱਕਵੇਂ ਹੋ ਸਕਣ ਵਾਲੇ ਭਾਗੀਦਾਰ ਫੈਮਿਲੀ ਡਾਕਟਰ ਲੱਭਣ ਵਿੱਚ ਮਦਦ ਕਰ ਸਕਦਾ ਹੈ ਅਤੇ ਬੇਨਤੀ ਦੀ ਪ੍ਰਕਿਰਿਆ ਵਿੱਚ ਤੁਹਾਡੀ ਰਹਿਨੁਮਾਈ ਕਰ ਸਕਦਾ ਹੈ।",
    privacy: "ਗੋਪਨੀਯਤਾ",
    accessibility: "ਪਹੁੰਚਯੋਗਤਾ",
    popoverTitle: "ਕਮਿਊਨਿਟੀ ਨੈਵੀਗੇਟਰ ਕੀ ਹੁੰਦਾ ਹੈ?",
    popoverBody:
      "ਕਮਿਊਨਿਟੀ ਨੈਵੀਗੇਟਰ Connected Care Halton ਨਾਲ ਮਿਲ ਕੇ ਉਹਨਾਂ ਲੋਕਾਂ ਦੀ ਮਦਦ ਕਰਦਾ ਹੈ ਜਿਨ੍ਹਾਂ ਨੂੰ ਫੈਮਿਲੀ ਡਾਕਟਰ ਲੱਭਣ ਲਈ ਸਹਾਇਤਾ ਦੀ ਲੋੜ ਹੁੰਦੀ ਹੈ। ਉਹ ਤੁਹਾਡੀਆਂ ਲੋੜਾਂ ਬਾਰੇ ਤੁਹਾਡੇ ਨਾਲ ਗੱਲ ਕਰ ਸਕਦਾ ਹੈ, ਉਹ ਭਾਗੀਦਾਰ ਫੈਮਿਲੀ ਡਾਕਟਰ ਲੱਭਣ ਵਿੱਚ ਮਦਦ ਕਰ ਸਕਦਾ ਹੈ ਜੋ ਤੁਹਾਡੇ ਲਈ ਢੁੱਕਵੇਂ ਹੋ ਸਕਦੇ ਹਨ, ਅਤੇ ਬੇਨਤੀ ਦੀ ਪ੍ਰਕਿਰਿਆ ਵਿੱਚ ਤੁਹਾਡੀ ਰਹਿਨੁਮਾਈ ਕਰ ਸਕਦਾ ਹੈ। ਨਵੇਂ ਮਰੀਜ਼ਾਂ ਨੂੰ ਸਵੀਕਾਰ ਕਰਨ ਬਾਰੇ ਅੰਤਿਮ ਫੈਸਲਾ ਫੈਮਿਲੀ ਡਾਕਟਰ ਜਾਂ ਕਲਿਨਿਕ ਕਰਦਾ ਹੈ।",
  },
  fr: {
    ...APPROVED_ENGLISH,
    heroTitle: "Trouvez un médecin de famille à Halton",
    heroSub:
      "Réduire les barrières du système de santé pour les residents d'Oakville, Halton Hills et Milton.",
    poweredBy: "Fourni par AcceptingMD.ca",
    step1Label: "Étape 1 sur 2",
    step1Title: "Où cherchez-vous un médecin de famille?",
    step1Sub:
      "Ce service gratuit aide les residents de Halton à trouver et à se connecter avec un médecin de famille acceptant de nouveaux patients.",
    step2Label: (city) => `Étape 2 sur 2 · ${city}`,
    step2Title: "Comment aimeriez-vous trouver un médecin?",
    changeCity: "← Changer de ville",
    step2Sub: "Choisissez l’option qui vous convient le mieux.",
    browseTitle: "Je souhaite chercher moi-même un médecin de famille.",
    browseSub:
      "Consultez les médecins de famille participants qui acceptent de nouvelles demandes de patients et soumettez une demande directement.",
    navTitle: "J’aimerais obtenir l’aide d’un navigateur communautaire.",
    navSub:
      "Un navigateur communautaire peut vous aider à repérer des médecins de famille participants qui pourraient répondre à vos besoins et vous guider dans le processus de demande.",
    privacy: "Confidentialité",
    accessibility: "Accessibilité",
    popoverTitle: "Qu’est-ce qu’un navigateur communautaire?",
    popoverBody:
      "Un navigateur communautaire travaille avec Connected Care Halton pour aider les personnes qui ont besoin de soutien pour trouver un médecin de famille. Il peut discuter avec vous de vos besoins, vous aider à repérer des médecins de famille participants qui pourraient vous convenir et vous guider dans le processus de demande. Le médecin de famille ou la clinique prend la décision finale quant à l’acceptation de nouveaux patients.",
  },
};

function Icon({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

function GlobeIcon() {
  return (
    <Icon className="cchoht-globe">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
      <path d="M2 12h20" />
    </Icon>
  );
}

function MapPinIcon() {
  return (
    <Icon className="cchoht-icon">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </Icon>
  );
}

function ArrowIcon({ className }: { className?: string }) {
  return (
    <Icon className={className ? `cchoht-arrow ${className}` : "cchoht-arrow"}>
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </Icon>
  );
}

function SearchIcon() {
  return (
    <Icon>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </Icon>
  );
}

function HandHelpingIcon() {
  return (
    <Icon>
      <path d="M11 12h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 14" />
      <path d="m7 18 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9" />
      <path d="m2 13 6 6" />
    </Icon>
  );
}

function InfoIcon() {
  return (
    <Icon>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </Icon>
  );
}

function EnglishText({
  activeLang,
  children,
}: {
  activeLang: LangCode;
  children: React.ReactNode;
}) {
  if (activeLang === "en") return <>{children}</>;
  return (
    <span lang="en" dir="ltr">
      {children}
    </span>
  );
}

function PoweredBy({ label }: { label: string }) {
  return (
    <Link to="/" className="cchoht-powered">
      <img src="/logo-1.png" alt="" width="18" height="18" />
      <span>{label}</span>
    </Link>
  );
}

const CchohtLandingPage: React.FC = () => {
  const [lang, setLang] = useState<LangCode>("en");
  const [step, setStep] = useState<0 | 1>(0);
  const [city, setCity] = useState<City | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const stepTitleRef = useRef<HTMLHeadingElement>(null);
  const skipStepFocus = useRef(true);
  const infoButtonRef = useRef<HTMLButtonElement>(null);
  const infoRootRef = useRef<HTMLDivElement>(null);

  const language = LANGS.find((item) => item.code === lang) || LANGS[0];
  const t = COPY[lang];

  useEffect(() => {
    if (skipStepFocus.current) {
      skipStepFocus.current = false;
      return;
    }
    stepTitleRef.current?.focus();
  }, [step]);

  useEffect(() => {
    if (!infoOpen) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setInfoOpen(false);
      infoButtonRef.current?.focus();
    };

    const onPointer = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (infoRootRef.current?.contains(target)) return;
      setInfoOpen(false);
    };

    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [infoOpen]);

  const selectCity = (nextCity: City) => {
    setInfoOpen(false);
    setCity(nextCity);
    setStep(1);
  };

  return (
    <div className="cchoht" dir={language.dir} lang={language.htmlLang}>
      <Helmet>
        <title>Find a Family Doctor in Halton | CCHOHT</title>
        <meta
          name="description"
          content="Reducing health system barriers for the people of Oakville, Halton Hills and Milton."
        />
        <meta property="og:title" content="Find a Family Doctor in Halton | CCHOHT" />
        <meta
          property="og:description"
          content="Reducing health system barriers for the people of Oakville, Halton Hills and Milton."
        />
        <meta name="twitter:title" content="Find a Family Doctor in Halton | CCHOHT" />
        <meta
          name="twitter:description"
          content="Reducing health system barriers for the people of Oakville, Halton Hills and Milton."
        />
      </Helmet>

      <div className="cchoht-langbar">
        <div className="cchoht-langbar-inner" role="group" aria-label="Language">
          <GlobeIcon />
          {LANGS.map((item) => (
            <button
              key={item.code}
              type="button"
              className="cchoht-lang"
              aria-pressed={lang === item.code}
              onClick={() => setLang(item.code)}
            >
              {item.native}
            </button>
          ))}
        </div>
      </div>

      <header className="cchoht-header">
        <div className="cchoht-header-inner">
          <img
            className="cchoht-logo"
            src="/cchoht-logo.png"
            width="5004"
            height="1167"
            alt="Connected Care Halton Ontario Health Team, Halton Hills, Milton, and Oakville"
          />
          <PoweredBy label={t.poweredBy} />
        </div>
      </header>

      <section className="cchoht-hero" aria-labelledby="cchoht-hero-title">
        <h1 id="cchoht-hero-title" className="cchoht-hero-title">
          {t.heroTitle}
        </h1>
        <p className="cchoht-hero-sub">{t.heroSub}</p>
      </section>

      <main className="cchoht-main">
        <div className="cchoht-panel">
          <div className="cchoht-dots" aria-hidden="true">
            {[0, 1].map((index) => {
              const dotClass =
                index === step
                  ? "cchoht-dot cchoht-dot-current"
                  : index < step
                    ? "cchoht-dot cchoht-dot-done"
                    : "cchoht-dot";
              return <span key={index} className={dotClass} />;
            })}
          </div>

          {step === 0 ? (
            <div className="cchoht-step" key="step-0">
              <p className="cchoht-step-kicker">
                <MapPinIcon />
                {t.step1Label}
              </p>
              <h2 className="cchoht-step-title" tabIndex={-1} ref={stepTitleRef}>
                {t.step1Title}
              </h2>
              <p className="cchoht-step-sub">{t.step1Sub}</p>
              <div className="cchoht-cities">
                {CITIES.map((item) => (
                  <button
                    key={item.slug}
                    type="button"
                    className="cchoht-city"
                    onClick={() => selectCity(item)}
                  >
                    <span className="cchoht-city-main">
                      <span className="cchoht-city-mark" aria-hidden="true">
                        {item.name.charAt(0)}
                      </span>
                      <span className="cchoht-city-name">{item.name}</span>
                    </span>
                    <ArrowIcon />
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {step === 1 && city ? (
            <div className="cchoht-step" key="step-1">
              <p className="cchoht-step-kicker">
                <MapPinIcon />
                {t.step2Label(city.name)}
              </p>
              <h2 className="cchoht-step-title" tabIndex={-1} ref={stepTitleRef}>
                {t.step2Title}
              </h2>
              <p className="cchoht-step-sub">{t.step2Sub}</p>
              <div className="cchoht-pathways">
                <Link className="cchoht-pathway" to={SEARCH_PATH[city.slug]}>
                  <span className="cchoht-pathway-icon" aria-hidden="true">
                    <SearchIcon />
                  </span>
                  <span className="cchoht-pathway-copy">
                    <span className="cchoht-pathway-title">{t.browseTitle}</span>
                    <span className="cchoht-pathway-desc">{t.browseSub}</span>
                  </span>
                  <ArrowIcon className="cchoht-pathway-arrow" />
                </Link>

                <article className="cchoht-pathway">
                  <span className="cchoht-pathway-icon" aria-hidden="true">
                    <HandHelpingIcon />
                  </span>
                  <div className="cchoht-pathway-copy" ref={infoRootRef}>
                    <p className="cchoht-pathway-title">
                      <Link
                        className="cchoht-pathway-link"
                        to={NAVIGATOR_PATH}
                        aria-describedby="cchoht-navigator-desc"
                      >
                        {t.navTitle}
                      </Link>
                      <button
                        ref={infoButtonRef}
                        type="button"
                        className="cchoht-info"
                        aria-expanded={infoOpen}
                        aria-controls="cchoht-navigator-info"
                        aria-label={t.popoverTitle}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setInfoOpen((open) => !open);
                        }}
                      >
                        <InfoIcon />
                      </button>
                    </p>
                    <div
                      id="cchoht-navigator-info"
                      role="region"
                      aria-labelledby="cchoht-navigator-info-title"
                      className="cchoht-popover"
                      hidden={!infoOpen}
                    >
                      <p id="cchoht-navigator-info-title" className="cchoht-popover-title">
                        {t.popoverTitle}
                      </p>
                      <p className="cchoht-popover-body">{t.popoverBody}</p>
                    </div>
                    <p id="cchoht-navigator-desc" className="cchoht-pathway-desc">
                      {t.navSub}
                    </p>
                  </div>
                  <ArrowIcon className="cchoht-pathway-arrow" />
                </article>
              </div>
              <button
                type="button"
                className="cchoht-change"
                onClick={() => {
                  setInfoOpen(false);
                  setStep(0);
                }}
              >
                {t.changeCity}
              </button>
            </div>
          ) : null}
        </div>
      </main>

      <footer className="cchoht-footer">
        <div className="cchoht-footer-inner">
          <div className="cchoht-footer-meta">
            <p className="cchoht-copy">
              <EnglishText activeLang={lang}>{t.footerCopy}</EnglishText>
            </p>
            <nav className="cchoht-footer-links">
              <Link to="/public/privacy">{t.privacy}</Link>
              <Link to="/public/accessibility">{t.accessibility}</Link>
            </nav>
          </div>
          <PoweredBy label={t.poweredBy} />
        </div>
      </footer>
    </div>
  );
};

export default CchohtLandingPage;
